import React from 'react';

import { Label } from '@eika/label';
import { ModalDialogType } from '@eika/modal';

import type { Account, AccountNumber } from '../../generated-types';
import AccountButton from './AccountButton';
import SelectAccountModal from './SelectAccountModal';

type RequestExceptionWithResponse = import('@eika-infrastruktur/request').RequestExceptionWithResponse;

interface Props {
    id: string;
    buttonLabel: string;
    title: string;
    account: Account | undefined;
    accountNumber: AccountNumber | undefined;
    accounts: Account[];
    invalidAccounts?: Account[];
    error?: string;
    onChange: (account: AccountNumber) => void;
    disabled: boolean;
    isLoading?: boolean;
    loadingError?: RequestExceptionWithResponse | null;
    refresh: () => void;
    showModal: boolean;
    setShowModal: (show: boolean) => void;
    customLabel?: ReturnType<typeof Label>;
    placeholder: string;
    locked?: boolean;
    bottomLabel?: ReturnType<typeof Label>;
    animateModalAs?: ModalDialogType;
    dataTestLabel?: string;
    showNegativeAmount?: boolean;
}

const AccountField: React.FC<Props> = ({
    id,
    buttonLabel,
    title,
    account,
    accounts,
    error,
    onChange,
    disabled,
    isLoading,
    loadingError,
    refresh,
    showModal = false,
    setShowModal,
    invalidAccounts,
    customLabel,
    placeholder,
    locked = false,
    bottomLabel,
    accountNumber,
    animateModalAs,
    dataTestLabel = 'from',
    showNegativeAmount = false,
}) => {
    const isBlocked = !!invalidAccounts?.find((account) => account.accountNumber.value === accountNumber?.value);
    return (
        <>
            <AccountButton
                account={account}
                accountNumber={accountNumber}
                label={buttonLabel}
                onClick={() => setShowModal(true)}
                error={error}
                disabled={disabled}
                customLabel={customLabel}
                placeholder={placeholder}
                locked={locked}
                showIcon
                bottomLabel={bottomLabel}
                dataTestLabel={dataTestLabel}
                showNegativeAmount={showNegativeAmount}
                isBlocked={isBlocked}
            />
            <SelectAccountModal
                id={id}
                error={loadingError ?? undefined}
                accounts={accounts}
                invalidAccounts={invalidAccounts}
                onChange={(newAccountNumber) => {
                    onChange(newAccountNumber);
                    setShowModal(false);
                }}
                setShow={setShowModal}
                show={showModal}
                retry={refresh}
                isLoading={isLoading}
                title={title}
                animateAs={animateModalAs}
                showNegativeAmount={showNegativeAmount}
            />
        </>
    );
};

export default AccountField;
